// SiteUP CRM (2026-09-18): seleção de cartões do Kanban.
// Lógica pura, sem Vue: recebe um Map (id em texto => item) e devolve outro
// Map. O estado reativo mora em composables/useKanbanSelection.js, que é só
// uma casca em cima destas funções. Assim a regra fica testável sem montar
// componente.

/**
 * Chave estável do cartão dentro da seleção. IDs chegam ora número, ora
 * texto (URL, dataset, API), então tudo vira texto.
 */
export const itemKey = item => {
  const raw = item && typeof item === 'object' ? item.id : item;
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
};

export const isSelected = (selection, item) => {
  const key = itemKey(item);
  if (!key || !selection) return false;
  return selection.has(key);
};

export const selectionCount = selection => (selection ? selection.size : 0);

export const selectedItems = selection =>
  selection ? Array.from(selection.values()) : [];

/** IDs numéricos, na ordem em que foram marcados. Descarta o que não é número. */
export const selectedIds = selection =>
  selectedItems(selection)
    .map(item => Number(item?.id))
    .filter(id => Number.isFinite(id));

/** Cartões com conversa vinculada: é o que o disparo em massa consegue usar. */
export const selectedItemsWithConversation = selection =>
  selectedItems(selection).filter(
    item => item?.item_details?.conversation_id != null
  );

export const toggleItem = (selection, item) => {
  const key = itemKey(item);
  if (!key) return selection;
  const next = new Map(selection);
  if (next.has(key)) next.delete(key);
  else next.set(key, item);
  return next;
};

export const addItems = (selection, items) => {
  const next = new Map(selection);
  (Array.isArray(items) ? items : []).forEach(item => {
    const key = itemKey(item);
    if (key) next.set(key, item);
  });
  return next;
};

export const removeItems = (selection, items) => {
  const next = new Map(selection);
  (Array.isArray(items) ? items : []).forEach(item => {
    const key = itemKey(item);
    if (key) next.delete(key);
  });
  return next;
};

/**
 * 'none' | 'partial' | 'all' para os itens de uma etapa. Etapa vazia é
 * 'none', nunca 'all': "Selecionar todos da etapa" numa coluna sem cartão
 * não pode parecer que já está tudo marcado.
 */
export const stageSelectionState = (selection, stageItems) => {
  const list = Array.isArray(stageItems) ? stageItems : [];
  if (!list.length) return 'none';
  let marked = 0;
  list.forEach(item => {
    if (isSelected(selection, item)) marked += 1;
  });
  if (marked === 0) return 'none';
  return marked === list.length ? 'all' : 'partial';
};

/** Marcar todos alterna: se a etapa já está inteira marcada, desmarca. */
export const toggleStage = (selection, stageItems) =>
  stageSelectionState(selection, stageItems) === 'all'
    ? removeItems(selection, stageItems)
    : addItems(selection, stageItems);

/**
 * Tira da seleção o que não existe mais no quadro (item excluído, movido de
 * funil ou filtrado). `knownIds` é a lista de ids visíveis; quando ela vem
 * vazia nada é removido, porque board vazio costuma ser carregamento.
 */
export const pruneSelection = (selection, knownIds) => {
  const known = (Array.isArray(knownIds) ? knownIds : []).map(String);
  if (!known.length) return selection;
  const keep = new Set(known);
  const next = new Map();
  selection.forEach((item, key) => {
    if (keep.has(key)) next.set(key, item);
  });
  return next;
};

export const clearSelection = () => new Map();
